<template>
  <div 
    class="login-container"
    :class="{ 'opacity-0': !backgroundLoaded }"
    :style="backgroundStyle"
  >
    <div class="login-form-container">
      <div class="max-w-md w-full space-y-6 sm:space-y-8 bg-white/95 dark:bg-gray-800/90 backdrop-blur-sm p-4 sm:p-6 md:p-8 rounded-lg shadow-lg transform transition-all duration-300 hover:shadow-2xl">
        <div>
          <h2 class="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-800 dark:text-white">
            K&C Portfolio Manager
          </h2>
          <p class="mt-2 text-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            Créer un compte
          </p>
        </div>
        <form class="mt-6 sm:mt-8 space-y-4 sm:space-y-6" @submit.prevent="handleSubmit">
          <div class="space-y-4">
            <div>
              <label for="email" class="sr-only">Adresse email</label>
              <input id="email" 
                     name="email" 
                     type="email" 
                     autocomplete="email" 
                     required 
                     v-model="email"
                     class="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 bg-white dark:bg-gray-700 placeholder-gray-500 text-gray-900 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm" 
                     placeholder="Adresse email">
            </div>
            <div>
              <label for="password" class="sr-only">Mot de passe</label>
              <input id="password" 
                     name="password" 
                     type="password" 
                     autocomplete="new-password" 
                     required 
                     v-model="password"
                     class="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 bg-white dark:bg-gray-700 placeholder-gray-500 text-gray-900 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm" 
                     placeholder="Mot de passe (8+ caractères)">
            </div>
            <div class="mt-2">
              <CaptchaWidget v-model="captchaToken" />
            </div>
          </div>

          <div v-if="error" class="bg-red-50 dark:bg-red-900/50 border border-red-400 text-red-700 dark:text-red-300 px-4 py-3 rounded relative" role="alert">
            <span class="block sm:inline">{{ error }}</span>
          </div>

          <div>
            <button type="submit" 
                    :disabled="isLoading"
                    class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
              <span v-if="isLoading" class="absolute left-0 inset-y-0 flex items-center pl-3">
                <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
              {{ isLoading ? 'Création du compte...' : "S'inscrire" }}
            </button>
          </div>

          <div class="mt-4 space-y-2">
            <router-link
              to="/login"
              class="btn-outline-primary w-full"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12H9m0 0l3-3m-3 3l3 3M4 6h16v12H4z" />
              </svg>
              <span>Déjà un compte ? Se connecter</span>
            </router-link>

            <router-link
              to="/resend-activation"
              class="btn-ghost-indigo w-full"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12H8m0 0l4-4m-4 4l4 4M4 6h16v12H4z" />
              </svg>
              <span>Renvoyer l'activation</span>
            </router-link>
          </div>
        </form>
      </div>
    </div>
    <AuthFooter @open-legal="isLegalModalOpen = true" />
    <LegalModal :is-open="isLegalModalOpen" @close="isLegalModalOpen = false" />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import axiosInstance from '@/config/axios'
import CaptchaWidget from '@/components/common/CaptchaWidget.vue'
import { useRouter } from 'vue-router'
import AuthFooter from '@/components/auth/AuthFooter.vue'
import LegalModal from '@/components/auth/LegalModal.vue'

export default {
  name: 'Register',
  components: { CaptchaWidget, AuthFooter, LegalModal },
  setup() {
    const router = useRouter()
    const email = ref('')
    const password = ref('')
    const captchaToken = ref('')
    const error = ref('')
    const isLoading = ref(false)
    const isLegalModalOpen = ref(false)
    const backgroundLoaded = ref(false)
    const backgroundStyle = ref({
      backgroundImage: '',
      backgroundColor: '#f3f4f6'
    })

    onMounted(async () => {
      try {
        const { default: bgImage } = await import('@/assets/backgroundImage.js')
        backgroundStyle.value.backgroundImage = `url(${bgImage})`
        backgroundLoaded.value = true
      } catch (err) {
        console.error('Erreur lors du chargement de l\'image:', err)
        backgroundLoaded.value = true
      }
    })

    const handleSubmit = async () => {
      try {
        error.value = ''
        isLoading.value = true
        if (!captchaToken.value && (import.meta.env.VITE_CAPTCHA_PROVIDER || '').length > 0) {
          error.value = 'Veuillez compléter le CAPTCHA.'
          return
        }
        await axiosInstance.post('/auth/register/', {
          email: email.value,
          password: password.value,
          captchaToken: captchaToken.value,
        })
        // Après inscription, rediriger vers la page de confirmation d'activation
        router.push({ path: '/activation-sent', query: { email: email.value } })
      } catch (err) {
        error.value = err.response?.data?.error || err.response?.data?.message || err.message || 'Une erreur est survenue lors de l\'inscription'
      } finally {
        isLoading.value = false
      }
    }

    return {
      email,
      password,
      captchaToken,
      error,
      isLoading,
      handleSubmit,
      backgroundLoaded,
      backgroundStyle,
      isLegalModalOpen
    }
  }
}
</script>

<style>
.login-container {
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: opacity 0.5s ease-in-out;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
}

.login-form-container {
  width: 100%;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
}

@media (min-width: 640px) {
  .login-form-container {
    padding: 1rem;
  }
}
</style>
